// ✅ FINAL: src/utils/completion.js - WITH PAYMENT SECURITY GATE ADDED (Hardened)
import { logger } from './logger';
import {
  getPositionsForFanType as _getPositionsForFanType,
  getCoachAreasForFanType as _getCoachAreasForFanType,
} from './unified-fan-requirements';

// Small helpers
const safeLen = (arr) => (Array.isArray(arr) ? arr.length : 0);
const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

// Safe wrappers (fallbacks if the unified module changes)
const getPositionsForFanType = (fanType) => {
  try {
    const v = _getPositionsForFanType?.(fanType);
    return Array.isArray(v) && v.length ? v : ['QB', 'DB']; // minimal safe default
  } catch {
    return ['QB', 'DB'];
  }
};
const getCoachAreasForFanType = (fanType) => {
  try {
    const v = _getCoachAreasForFanType?.(fanType);
    return Array.isArray(v) && v.length
      ? v
      : ['HC_leadership', 'HC_gameManagement', 'HC_playerDevelopment'];
  } catch {
    return ['HC_leadership', 'HC_gameManagement', 'HC_playerDevelopment'];
  }
};

// ✅ MAIN COMPLETION CALCULATOR: Use this everywhere
export const calculateCompletion = (gameData) => {
  try {
    const positionGrades = gameData?.positionGrades || {};
    const coachGrades = gameData?.coachGrades || {};
    const predictions = gameData?.predictions || {};
    const fanType = gameData?.fanType || 'casual';

    // Get requirements using unified functions (with safe fallbacks)
    const requiredPositions = getPositionsForFanType(fanType);
    const requiredCoachAreas = getCoachAreasForFanType(fanType);

    logger?.completion?.('🎯 Calculating completion for:', {
      fanType,
      requiredPositions,
      requiredCoachAreas,
      positionGradesKeys: Object.keys(positionGrades),
      coachGradesKeys: Object.keys(coachGrades),
      predictionsKeys: Object.keys(predictions),
    });

    // Completed positions: needs a non-empty grade like "QB_grade"
    const completedPositions = requiredPositions.filter((position) => {
      const key = `${position}_grade`;
      const val = positionGrades[key];
      const hasGrade =
        typeof val === 'string' && val.trim() !== '' && val !== 'undefined';
      if (hasGrade) logger?.completion?.(`✅ Position ${position}: ${val}`);
      return hasGrade;
    }).length;

    // Completed coach areas
    const completedCoachAreas = requiredCoachAreas.filter((area) => {
      const val = coachGrades[area];
      const hasGrade =
        typeof val === 'string' && val.trim() !== '' && val !== 'undefined';
      if (hasGrade) logger?.completion?.(`✅ Coach ${area}: ${val}`);
      return hasGrade;
    }).length;

    // Prediction status (4 buckets total; coach rankings optional for NFL)
    const coachRankingApplicable = !(String(gameData?.league || '').toUpperCase() === 'NFL');
    const predictionStatus = {
      gameResults:
        !!predictions.gameResults &&
        Object.keys(predictions.gameResults).length > 0,
      topPlayer:
        !!predictions.topPlayer &&
        (predictions.topPlayer.offensive || predictions.topPlayer.defensive),
      coachRankings:
        coachRankingApplicable &&
        !!predictions.conferenceCoachRankings &&
        Object.keys(predictions.conferenceCoachRankings).length > 0,
      seasonOutlook:
        !!predictions.seasonOutlook &&
        typeof predictions.seasonOutlook === 'string' &&
        predictions.seasonOutlook.trim().length > 0,
    };
    const completedPredictions = Object.values(predictionStatus).filter(Boolean).length;

    const requiredPredictionBuckets = coachRankingApplicable ? 4 : 3;

    // Percentages (safe div)
    const positionsPercentage = pct(completedPositions, safeLen(requiredPositions));
    const coachesPercentage = pct(
      completedCoachAreas,
      safeLen(requiredCoachAreas)
    );
    const predictionsPercentage = pct(completedPredictions, requiredPredictionBuckets);

    // Overall completion percentage
    const overallCompletionPercentage = Math.round(
      (positionsPercentage + coachesPercentage + predictionsPercentage) / 3
    );

    const result = {
      completionPercentage: Number.isFinite(overallCompletionPercentage)
        ? overallCompletionPercentage
        : 0,
      isSubmitted:
        gameData?.status === 'submitted' || Boolean(gameData?.submittedAt),
      isComplete:
        completedPositions === safeLen(requiredPositions) &&
        completedCoachAreas === safeLen(requiredCoachAreas) &&
        completedPredictions >= requiredPredictionBuckets,

      // Position details
      positions: {
        completed: completedPositions,
        required: safeLen(requiredPositions),
        isComplete: completedPositions === safeLen(requiredPositions),
        percentage: positionsPercentage,
      },

      // Coach details
      coaches: {
        completed: completedCoachAreas,
        required: safeLen(requiredCoachAreas),
        isComplete: completedCoachAreas === safeLen(requiredCoachAreas),
        percentage: coachesPercentage,
      },

      // Prediction details
      predictions: {
        ...predictionStatus,
        completed: completedPredictions,
        required: requiredPredictionBuckets,
        isComplete: completedPredictions >= requiredPredictionBuckets,
        percentage: predictionsPercentage,
      },

      // Additional details (legacy/back-compat)
      completedPositions,
      completedCoachAreas,
      requiredPositions: safeLen(requiredPositions),
      requiredCoaches: safeLen(requiredCoachAreas),
      totalCompleted: completedPositions + completedCoachAreas,
      totalRequired: safeLen(requiredPositions) + safeLen(requiredCoachAreas),

      positionDetails: requiredPositions.map((pos) => {
        const key = `${pos}_grade`;
        const val = positionGrades[key];
        const done = typeof val === 'string' && val.trim() !== '';
        return { position: pos, completed: done, grade: done ? val : null };
      }),
      coachDetails: requiredCoachAreas.map((area) => {
        const val = coachGrades[area];
        const done = typeof val === 'string' && val.trim() !== '';
        return { area, completed: done, grade: done ? val : null };
      }),
    };

    logger?.completion?.('🎯 Completion Calculated:', {
      fanType,
      positions: `${completedPositions}/${safeLen(
        requiredPositions
      )} (${positionsPercentage}%)`,
      coaches: `${completedCoachAreas}/${safeLen(
        requiredCoachAreas
      )} (${coachesPercentage}%)`,
      predictions: `${completedPredictions}/4 (${predictionsPercentage}%)`,
      overall: `${result.completionPercentage}%`,
    });

    return result;
  } catch (error) {
    console.error('❌ Error calculating completion:', error);
    return getDefaultCompletion();
  }
};

// Default completion object
const getDefaultCompletion = () => ({
  completionPercentage: 0,
  isSubmitted: false,
  isComplete: false,
  positions: { completed: 0, required: 9, isComplete: false, percentage: 0 },
  coaches: { completed: 0, required: 9, isComplete: false, percentage: 0 },
  predictions: {
    gameResults: false,
    topPlayer: false,
    coachRankings: false,
    seasonOutlook: false,
    completed: 0,
    required: 4,
    isComplete: false,
    percentage: 0,
  },
  completedPositions: 0,
  completedCoachAreas: 0,
  requiredPositions: 9,
  requiredCoaches: 9,
  totalCompleted: 0,
  totalRequired: 18,
  positionDetails: [],
  coachDetails: [],
});

// Backward compatibility
export const calculateCompletionStatusDetailed = calculateCompletion;

// 🔥 CRITICAL FIX: Save validation WITH payment security gate
export const shouldSaveGameData = (gameData, user) => {
  try {
    // Basic validation checks
    if (!user || user.isAnonymous) {
      console.log('⏭️ Firebase: Skipping save - no authenticated user');
      return false;
    }
    if (gameData?.isSubmitting) {
      console.log('⏭️ Firebase: Skipping save - currently submitting');
      return false;
    }
    if (!gameData?.team || gameData.team.trim() === '' || gameData.team === 'unknown') {
      console.log('⏭️ Firebase: Skipping save - no team selected');
      return false;
    }

    // 🔒 Block auto-save during payment flow (disabled for v2 launch)
    if (gameData?._paymentFlowActive || gameData?._redirectingToStripe) {
      console.log('🚫 PAYMENT FLOW: Blocking save during payment redirect to prevent bypass');
      return false;
    }

    // 🔥 PAYMENT SECURITY GATE: temporarily disabled to ensure persistence during v2 rollout
    // Define safe fallbacks to avoid ReferenceError in logs below
    const isPremiumGame = false;
    const isNewGame = false;
    // const isPremiumGame =
    //   !!gameData?.fanType &&
    //   ['dedicated', 'expert', 'diehard'].includes(gameData.fanType);
    // const isNewGame =
    //   !gameData?._existingDocId && !gameData?._continuingExisting && !gameData?.id;
    // if (isPremiumGame && isNewGame) { ... }

    // Minimal progress check (any meaningful data)
    const hasBasicProgress =
      (gameData.team && gameData.team.trim().length > 0) ||
      (gameData.participantName && gameData.participantName.trim().length > 0) ||
      (gameData.fanType && gameData.fanType !== '') ||
      (gameData.positionGrades && Object.keys(gameData.positionGrades).length > 0) ||
      (gameData.coachGrades && Object.keys(gameData.coachGrades).length > 0) ||
      (gameData.predictions &&
        Object.values(gameData.predictions).some(
          (pred) => pred && typeof pred === 'object' && Object.keys(pred).length > 0
        )) ||
      gameData.groupId ||
      (gameData.completionPercentage && gameData.completionPercentage > 0);

    if (!hasBasicProgress) {
      console.log('⏭️ Firebase: Skipping save - no meaningful progress to preserve');
      return false;
    }

    console.log('✅ Firebase: Save validation passed', {
      team: gameData.team,
      fanType: gameData.fanType,
      isPremium: isPremiumGame,
      isNew: isNewGame,
      hasBasicProgress,
      paymentStatus: isPremiumGame ? 'validated' : 'not-required',
    });

    return true;
  } catch (error) {
    console.error('❌ Error in shouldSaveGameData:', error);
    return false;
  }
};

// 🔥 NEW: Debug function to test specific game data
if (typeof window !== 'undefined') {
  window.debugSaveValidation = (gameData, user) => {
    console.log('🔍 === DEBUGGING SAVE VALIDATION ===');

    if (!gameData) {
      gameData = window.gameData || {
        team: 'Test Team',
        fanType: 'casual',
        participantName: 'Test User',
      };
      console.log('Using test game data:', gameData);
    }

    if (!user) {
      user = window.auth?.currentUser || { uid: 'test-user', isAnonymous: false };
      console.log('Using test user:', user);
    }

    const result = shouldSaveGameData(gameData, user);

    console.log('🎯 VALIDATION RESULT:', result ? '✅ SAVE ALLOWED' : '🚫 SAVE BLOCKED');

    // Breakdown
    console.log('\n📋 Validation Breakdown:');
    console.log('1. User check:', !user?.isAnonymous ? '✅ PASS' : '❌ FAIL');
    console.log('2. Team check:', gameData.team ? '✅ PASS' : '❌ FAIL');
    console.log(
      '3. Payment flow check:',
      !gameData._paymentFlowActive ? '✅ PASS' : '❌ FAIL'
    );

    const isPremiumGame =
      !!gameData?.fanType &&
      ['dedicated', 'expert', 'diehard'].includes(gameData.fanType);
    const isNewGame =
      !gameData?._existingDocId && !gameData?._continuingExisting && !gameData?.id;

    if (isPremiumGame && isNewGame) {
      const hasValidPayment = Boolean(
        gameData.creditConsumed ||
          gameData._creditConsumedSuccessfully ||
          gameData.paymentJustCompleted ||
          gameData._paymentValidated ||
          gameData._alreadyPaid ||
          gameData._skipPayment
      );
      console.log('4. Premium payment check:', hasValidPayment ? '✅ PASS' : '❌ FAIL');
    } else {
      console.log('4. Premium payment check: ✅ SKIP (not required)');
    }

    const hasBasicProgress =
      (gameData.team && gameData.teamId) ||
      (gameData.participantName && gameData.participantName.trim().length > 0) ||
      (gameData.positionGrades && Object.keys(gameData.positionGrades).length > 0) ||
      (gameData.fanType && gameData.fanType !== '');
    console.log('5. Progress check:', hasBasicProgress ? '✅ PASS' : '❌ FAIL');

    return result;
  };
}

export const enhancedCleanupAbandonedPaymentData = (reason = 'manual') => {
  try {
    console.log(`🧹 Enhanced cleanup of abandoned payment data (reason: ${reason})...`);

    // Snapshot before clearing
    const tempGameData = localStorage.getItem('sbe-temp-game-data');
    const paymentTimestamp = localStorage.getItem('sbe-payment-timestamp');
    const paymentAttemptId = localStorage.getItem('sbe-payment-attempt-id');

    const report = {
      reason,
      timestamp: Date.now(),
      hadTempData: !!tempGameData,
      hadPaymentTimestamp: !!paymentTimestamp,
      hadAttemptId: !!paymentAttemptId,
      timeSincePayment: paymentTimestamp ? Date.now() - parseInt(paymentTimestamp, 10) : null,
    };

    if (tempGameData) {
      try {
        const parsed = JSON.parse(tempGameData);
        report.gameData = {
          team: parsed.team,
          fanType: parsed.fanType,
          participantName: parsed.participantName,
          paymentAttemptId: parsed._paymentAttemptId,
        };
      } catch {
        report.parseError = true;
      }
    }

    // LocalStorage keys
    [
      'sbe-temp-game-data',
      'sbe-temp-fan-type',
      'sbe-temp-current-step',
      'sbe-payment-timestamp',
      'sbe-payment-attempt-id',
      'sbe-navigation-context',
    ].forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        report[`cleared_${key}`] = true;
      }
    });

    // SessionStorage keys
    [
      'stripe-payment-active',
      'stripe-payment-start',
      'payment_success_detected',
      'payment-security-token',
    ].forEach((key) => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        report[`cleared_${key}`] = true;
      }
    });

    console.log('✅ Enhanced payment abandonment cleanup completed:', report);

    if (report.timeSincePayment && report.timeSincePayment < 300000) {
      console.warn('🚨 ADMIN ALERT: Recent payment attempt cleaned up', report);
    }

    return report;
  } catch (error) {
    console.error('❌ Error in enhanced payment cleanup:', error);
    return { success: false, error: error?.message || String(error) };
  }
};

// Status helper
export const getGameStatus = (gameData) => {
  if (gameData?.submittedAt || gameData?.status === 'submitted') return 'submitted';
  const completion = calculateCompletion(gameData);
  if (completion.completionPercentage > 10) return 'in_progress';
  return 'draft';
};

// Display text helper
export const getCompletionDisplayText = (completion) => {
  if (completion?.isSubmitted) return `Submitted (${completion.completionPercentage}%)`;
  const p = completion?.completionPercentage ?? 0;
  if (p === 0) return 'Just Started';
  if (p < 25) return `Getting Started (${p}%)`;
  if (p < 50) return `Making Progress (${p}%)`;
  if (p < 75) return `Good Progress (${p}%)`;
  if (p < 100) return `Almost Done (${p}%)`;
  return `Complete (${p}%)`;
};

// Debug helper
export const debugCompletionStatus = (gameData) => {
  const result = calculateCompletion(gameData);
  console.log('🔍 Completion Status Debug:', {
    fanType: gameData?.fanType,
    positionGrades: Object.keys(gameData?.positionGrades || {}),
    coachGrades: Object.keys(gameData?.coachGrades || {}),
    predictions: gameData?.predictions,
    result,
  });
  return result;
};

// 🔥 NEW: Security audit function for debugging
export const auditGamePaymentStatus = (gameData) => {
  console.log('🔍 === GAME PAYMENT AUDIT ===');
  console.log('📋 Game Data Summary:', {
    team: gameData?.team,
    fanType: gameData?.fanType,
    participantName: gameData?.participantName,
    hasExistingId: Boolean(gameData?._existingDocId || gameData?.id),
    status: gameData?.status,
  });

  const isPremiumGame =
    !!gameData?.fanType && ['dedicated', 'expert', 'diehard'].includes(gameData.fanType);
  const isNewGame =
    !gameData?._existingDocId && !gameData?._continuingExisting && !gameData?.id;

  console.log('🎯 Payment Analysis:', {
    isPremiumGame,
    isNewGame,
    needsPaymentValidation: isPremiumGame && isNewGame,
  });

  if (isPremiumGame && isNewGame) {
    const paymentFlags = {
      creditConsumed: !!gameData?.creditConsumed,
      creditConsumedSuccessfully: !!gameData?._creditConsumedSuccessfully,
      paymentJustCompleted: !!gameData?.paymentJustCompleted,
      paymentValidated: !!gameData?._paymentValidated,
      alreadyPaid: !!gameData?._alreadyPaid,
      skipPayment: !!gameData?._skipPayment,
    };
    const hasValidPayment = Object.values(paymentFlags).some(Boolean);

    console.log('💳 Payment Flags:', paymentFlags);
    console.log('✅ Has Valid Payment:', hasValidPayment);
    console.log('🚨 Would Block Save:', !hasValidPayment);

    return {
      isPremiumGame,
      isNewGame,
      needsPaymentValidation: true,
      paymentFlags,
      hasValidPayment,
      wouldBlockSave: !hasValidPayment,
      recommendation: hasValidPayment ? 'SAVE ALLOWED' : 'SAVE BLOCKED - PAYMENT REQUIRED',
    };
  }

  console.log('✅ No payment validation needed');
  return {
    isPremiumGame,
    isNewGame,
    needsPaymentValidation: false,
    hasValidPayment: true,
    wouldBlockSave: false,
    recommendation: 'SAVE ALLOWED - NO PAYMENT REQUIRED',
  };
};

// Expose helpers for debugging (browser only)
if (typeof window !== 'undefined') {
  window.auditGamePaymentStatus = auditGamePaymentStatus;
  window.shouldSaveGameData = shouldSaveGameData;
  window.calculateCompletion = calculateCompletion;
  window.cleanupAbandonedPaymentData = enhancedCleanupAbandonedPaymentData;

  console.log('🧪 Testing functions exposed globally:', {
    shouldSaveGameData: typeof window.shouldSaveGameData,
    calculateCompletion: typeof window.calculateCompletion,
    cleanupAbandonedPaymentData: typeof window.cleanupAbandonedPaymentData,
  });
}

// Alias export for consistency
export const cleanupAbandonedPaymentData = enhancedCleanupAbandonedPaymentData;
